import type { Application } from '@labeling/globals';
import { Button, Tag, TextField, Tooltip, UtilityButton } from '@lego/connect-components-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApplicationAccess, Selection } from './types';

export const AppTreeItem = ({
  access,
  isOpen,
  selection,
  newGroupApp,
  newGroupName,
  onToggle,
  onSelectGroup,
  onSelectUnrestricted,
  onStartNewGroup,
  onCommitNewGroup,
  onCancelNewGroup,
  onNewGroupNameChange,
}: {
  access: ApplicationAccess;
  isOpen: boolean;
  selection: Selection;
  newGroupApp: Application | null;
  newGroupName: string;
  onToggle: () => void;
  onSelectGroup: (app: Application, groupName: string) => void;
  onSelectUnrestricted: (app: Application) => void;
  onStartNewGroup: () => void;
  onCommitNewGroup: () => void;
  onCancelNewGroup: () => void;
  onNewGroupNameChange: (value: string) => void;
}) => {
  const { t } = useTranslation();
  const { application: app, groups, unrestricted } = access;
  const panelId = useId();
  const memberCount = new Set([
    ...unrestricted.map((operator) => operator.id),
    ...[...groups.values()].flatMap((members) => members.map((member) => member.operatorId)),
  ]).size;
  const sortedGroups = [...groups].sort(([a], [b]) => a.localeCompare(b));
  const isUnrestrictedSelected =
    selection?.application === app && selection.kind === 'unrestricted';

  return (
    <div data-testid="application-tree-item" className="rounded-lg border border-core-slate-100">
      <div className="flex items-center gap-100 px-100 py-50 bg-core-slate-50 rounded-lg">
        <Button
          variant="neutral"
          appearance="ghost"
          fullWidth
          className="!justify-start min-w-0 flex-1 [&>span]:!justify-start [&>span]:!text-left"
          aria-expanded={isOpen}
          aria-controls={isOpen ? panelId : undefined}
          iconName={isOpen ? 'chevron-down' : 'chevron-right'}
          onClick={onToggle}
        >
          {app}
        </Button>
        <Tag
          variant="neutral"
          size="small"
          aria-label={t('operatorAccessPage.display.memberCountTooltip', { count: memberCount })}
          title={t('operatorAccessPage.display.memberCountTooltip', { count: memberCount })}
        >
          {String(memberCount)}
        </Tag>
      </div>

      {isOpen && (
        <section id={panelId} aria-label={app} className="ml-200 my-100 mr-100 pl-100 space-y-50">
          <div className="flex items-center gap-100">
            <div className="flex-1 min-w-0">
              <Button
                variant={isUnrestrictedSelected ? 'primary' : 'neutral'}
                appearance={isUnrestrictedSelected ? 'outline' : 'ghost'}
                fullWidth
                className="!justify-start [&>span]:!justify-start [&>span]:!text-left"
                aria-current={isUnrestrictedSelected ? 'true' : undefined}
                onClick={() => onSelectUnrestricted(app)}
              >
                {t('operatorAccessPage.display.unrestrictedGroup')}
              </Button>
            </div>
            <Tooltip position="right">
              <Tooltip.Trigger>
                <UtilityButton
                  iconName="circle-info"
                  size="small"
                  appearance="ghost"
                  aria-label={t('operatorAccessPage.display.unrestrictedTooltip')}
                />
              </Tooltip.Trigger>
              <Tooltip.Content>
                {t('operatorAccessPage.display.unrestrictedTooltip')}
              </Tooltip.Content>
            </Tooltip>
            <Tag
              variant="neutral"
              size="small"
              aria-label={t('operatorAccessPage.display.memberCountTooltip', {
                count: unrestricted.length,
              })}
              title={t('operatorAccessPage.display.memberCountTooltip', {
                count: unrestricted.length,
              })}
            >
              {String(unrestricted.length)}
            </Tag>
          </div>

          {sortedGroups.map(([groupName, list]) => {
            const isSelected =
              selection?.application === app &&
              selection.kind === 'group' &&
              selection.groupName === groupName;
            return (
              <div key={groupName} className="flex items-center gap-100">
                <div className="flex-1 min-w-0">
                  <Button
                    variant={isSelected ? 'primary' : 'neutral'}
                    appearance={isSelected ? 'outline' : 'ghost'}
                    fullWidth
                    className="!justify-start [&>span]:!justify-start [&>span]:!text-left"
                    aria-current={isSelected ? 'true' : undefined}
                    onClick={() => onSelectGroup(app, groupName)}
                  >
                    {groupName}
                  </Button>
                </div>
                <Tag
                  variant="neutral"
                  size="small"
                  aria-label={t('operatorAccessPage.display.memberCountTooltip', {
                    count: list.length,
                  })}
                  title={t('operatorAccessPage.display.memberCountTooltip', {
                    count: list.length,
                  })}
                >
                  {String(list.length)}
                </Tag>
              </div>
            );
          })}
          <div className="flex justify-start">
            <Button
              variant="neutral"
              appearance="outline"
              size="small"
              iconName="circle-plus"
              onClick={onStartNewGroup}
            >
              {t('operatorAccessPage.action.newGroup')}
            </Button>
          </div>
          {newGroupApp === app && (
            <div className="flex flex-wrap items-center gap-50 py-50">
              <div className="w-full">
                <TextField
                  size="medium"
                  value={newGroupName}
                  onChange={(_e, value) => onNewGroupNameChange(value)}
                  placeholder={t('operatorAccessPage.display.newGroupPlaceholder')}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onCommitNewGroup();
                    if (e.key === 'Escape') onCancelNewGroup();
                  }}
                />
              </div>
              <Button variant="primary" size="small" onClick={onCommitNewGroup}>
                {t('operatorAccessPage.action.add')}
              </Button>
              <Button variant="neutral" appearance="ghost" size="small" onClick={onCancelNewGroup}>
                {t('operatorAccessPage.action.cancel')}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
};
